import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HitsterEvent, EventTeam, EventRound } from '../types';
import { getEventTeamStandings, type EventTeamStanding } from '../utils/eventScores';
import './ParticipantScores.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ParticipantScores = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [standings, setStandings] = useState<EventTeamStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<EventTeamStanding | null>(null);
  const [selectedRound, setSelectedRound] = useState<EventRound | null>(null);

  // Reactions state
  const [reactions, setReactions] = useState<{ id: string; naam: string; bericht: string; aangemaakt: string }[]>([]);
  const [reactieNaam, setReactieNaam] = useState('');
  const [reactieBericht, setReactieBericht] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [reactieStatus, setReactieStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const [reactieError, setReactieError] = useState('');

  useEffect(() => {
    const fetchEvent = async (id: string) => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${id}`);
        if (!response.ok) throw new Error('Event not found');
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        console.error('Error fetching event:', err);
      }
      setLoading(false);
    };

    const fetchLatestEvent = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events`);
        if (!response.ok) throw new Error('Failed to fetch events');
        const events: HitsterEvent[] = await response.json();
        if (events.length === 0) { setLoading(false); return; }
        // Use the most recently updated event
        const latest = events.sort((a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )[0];
        await fetchEvent(latest.id);
      } catch (err) {
        console.error('Error fetching events:', err);
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent(eventId);
      const interval = setInterval(() => fetchEvent(eventId), 30000);
      return () => clearInterval(interval);
    } else {
      fetchLatestEvent();
      const interval = setInterval(fetchLatestEvent, 30000);
      return () => clearInterval(interval);
    }
  }, [eventId]);

  useEffect(() => {
    if (!event) return;

    setStandings(getEventTeamStandings(event));
  }, [event]);

  // Fetch approved reactions when a team is selected
  useEffect(() => {
    if (!selectedTeam || !event) { setReactions([]); return; }
    const fetchReactions = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/reactions/${event.id}/${selectedTeam.team.id}`);
        if (res.ok) setReactions(await res.json());
      } catch { /* ignore */ }
    };
    fetchReactions();
  }, [selectedTeam, event]);

  const submitReactie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeam || !event) return;
    setReactieStatus('sending');
    setReactieError('');
    try {
      const res = await fetch(`${API_BASE}/api/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: reactieNaam,
          bericht: reactieBericht,
          teamId: selectedTeam.team.id,
          teamName: selectedTeam.team.name,
          eventId: event.id,
          _honeypot: honeypot,
        }),
      });
      if (res.ok) {
        setReactieStatus('ok');
        setReactieNaam('');
        setReactieBericht('');
        setHoneypot('');
      } else {
        const data = await res.json().catch(() => ({}));
        setReactieError(data.error || 'Er ging iets mis');
        setReactieStatus('error');
      }
    } catch {
      setReactieError('Kan geen verbinding maken');
      setReactieStatus('error');
    }
  };

  const getRoundDetail = (team: EventTeam, round: EventRound) => {
    return team.roundData?.find(rd => rd.roundId === round.id) || null;
  };

  const rankEmoji = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}.`;
  };

  if (loading) {
    return (
      <div className="participant-scores loading">
        <div className="loading-spinner">🎵</div>
        <p>Laden...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="participant-scores error">
        <p>Evenement niet gevonden</p>
      </div>
    );
  }

  const rounds = event.rounds || [];

  // Round detail view
  if (selectedTeam && selectedRound) {
    const roundData = getRoundDetail(selectedTeam.team, selectedRound);
    const songs = selectedRound.songs.slice().sort((a, b) => a.order - b.order);
    const currentRoundIndex = rounds.findIndex(r => r.id === selectedRound.id);
    const prevRound = currentRoundIndex > 0 ? rounds[currentRoundIndex - 1] : null;
    const nextRound = currentRoundIndex < rounds.length - 1 ? rounds[currentRoundIndex + 1] : null;

    return (
      <div className="participant-scores">
        <div className="ps-header">
          <button className="ps-back-btn" onClick={() => setSelectedRound(null)}>
            ← Terug
          </button>
          <div className="ps-header-info">
            <div className="ps-team-name">{selectedTeam.team.name}</div>
            <div className="ps-round-name">{selectedRound.name}</div>
          </div>
          <div className="ps-round-score">
            {roundData?.totalScore || 0} pt
          </div>
        </div>

        <div className="ps-songs-list">
          {songs.map((song, index) => {
            const songScore = roundData?.songScores.find(ss => ss.songId === song.id);
            const points = songScore?.points || 0;
            return (
              <div key={song.id} className={`ps-song-row ${points > 0 ? 'has-points' : 'no-points'}`}>
                <div className="ps-song-number">{index + 1}</div>
                <div className="ps-song-info">
                  <div className="ps-song-title">{song.title}</div>
                  <div className="ps-song-artist">{song.artist} — {song.year}</div>
                  <div className="ps-song-checks">
                    <span className={`ps-check ${songScore?.titleCorrect ? 'correct' : 'wrong'}`}>
                      {songScore?.titleCorrect ? '✓' : '✗'} Titel
                    </span>
                    <span className={`ps-check ${songScore?.artistCorrect ? 'correct' : 'wrong'}`}>
                      {songScore?.artistCorrect ? '✓' : '✗'} Artiest
                    </span>
                    <span className={`ps-check ${songScore?.yearCorrect ? 'correct' : 'wrong'}`}>
                      {songScore?.yearCorrect ? '✓' : '✗'} Jaar
                    </span>
                    <span className={`ps-check ${songScore?.positionCorrect ? 'correct' : 'wrong'}`}>
                      {songScore?.positionCorrect ? '✓' : '✗'} Positie
                    </span>
                  </div>
                </div>
                <div className="ps-song-points">{points} pt</div>
              </div>
            );
          })}
        </div>

        <div className="ps-round-total">
          Totaal {selectedRound.name}: <strong>{roundData?.totalScore || 0} punten</strong>
        </div>

        <div className="ps-round-nav">
          <button
            className="ps-round-nav-btn"
            onClick={() => setSelectedRound(prevRound!)}
            disabled={!prevRound}
          >
            ← {prevRound ? prevRound.name : 'Vorige'}
          </button>
          <button
            className="ps-round-nav-btn"
            onClick={() => setSelectedRound(nextRound!)}
            disabled={!nextRound}
          >
            {nextRound ? nextRound.name : 'Volgende'} →
          </button>
        </div>
      </div>
    );
  }

  // Team detail view (rounds overview)
  if (selectedTeam) {
    return (
      <div className="participant-scores">
        <div className="ps-header">
          <button className="ps-back-btn" onClick={() => setSelectedTeam(null)}>
            ← Terug
          </button>
          <div className="ps-header-info">
            <div className="ps-team-name">{selectedTeam.team.name}</div>
            <div className="ps-position">{rankEmoji(selectedTeam.rank)} Positie {selectedTeam.rank}</div>
          </div>
          <div className="ps-total-score">
            {selectedTeam.totalScore} pt
          </div>
        </div>

        <div className="ps-team-detail-layout">
          <div className="ps-team-detail-left">
            <div className="ps-rounds-list">
              {rounds.map(round => {
                const rs = selectedTeam.roundScores.find(r => r.roundId === round.id);
                const score = rs?.score || 0;
                return (
                  <button
                    key={round.id}
                    className="ps-round-row"
                    onClick={() => setSelectedRound(round)}
                  >
                    <div className="ps-round-name-col">{round.name}</div>
                    <div className="ps-round-score-col">
                      <span className="ps-round-pts">{score} pt</span>
                      <span className="ps-chevron">›</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="ps-team-total">
              Totaal: <strong>{selectedTeam.totalScore} punten</strong>
            </div>
          </div>

          {/* Reactions section */}
          <div className="ps-reactions">
          <h3 className="ps-reactions-title">💬 Laat weten wat je ervan vond</h3>

          {reactions.length > 0 && (
            <div className="ps-reactions-list">
              {reactions.map(r => (
                <div key={r.id} className="ps-reaction-item">
                  <div className="ps-reaction-naam">{r.naam}</div>
                  <div className="ps-reaction-bericht">{r.bericht}</div>
                  <div className="ps-reaction-datum">{new Date(r.aangemaakt).toLocaleDateString('nl-NL')}</div>
                </div>
              ))}
            </div>
          )}

          {reactieStatus === 'ok' ? (
            <div className="ps-reaction-ok">✓ Reactie ingediend — wordt zichtbaar na goedkeuring.</div>
          ) : (
            <form className="ps-reaction-form" onSubmit={submitReactie}>
              <input
                type="text"
                className="ps-reaction-input"
                placeholder="Jouw naam"
                value={reactieNaam}
                onChange={e => setReactieNaam(e.target.value)}
                maxLength={100}
                required
              />
              <textarea
                className="ps-reaction-textarea"
                placeholder="Schrijf een reactie..."
                value={reactieBericht}
                onChange={e => setReactieBericht(e.target.value)}
                maxLength={300}
                rows={3}
                required
              />
              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                className="ps-honeypot"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
              />
              {reactieStatus === 'error' && <div className="ps-reaction-error">{reactieError}</div>}
              <button
                type="submit"
                className="ps-reaction-submit"
                disabled={reactieStatus === 'sending'}
              >
                {reactieStatus === 'sending' ? 'Versturen...' : 'Reactie sturen'}
              </button>
            </form>
          )}
          </div>
        </div>
      </div>
    );
  }

  // Main scoreboard view
  return (
    <div className="participant-scores">
      <div className="ps-title-header">
        <a href="https://tveurhof.nl" className="ps-back-home">← tveurhof.nl</a>
        <h1>🏆 {event.name}</h1>
        <p className="ps-subtitle">Klik op je team voor details per ronde</p>
      </div>

      <div className="ps-main-layout">
        <div className="ps-standings">
          {standings.map((standing) => (
            <button
              key={standing.team.id}
              className={`ps-standing-row rank-${Math.min(standing.rank, 4)}`}
              onClick={() => setSelectedTeam(standing)}
            >
              <div className="ps-rank">{rankEmoji(standing.rank)}</div>
              <div className="ps-standing-name">{standing.team.name}</div>
              <div className="ps-standing-score">
                {standing.totalScore} pt
                <span className="ps-chevron">›</span>
              </div>
            </button>
          ))}
        </div>

        {event.announcementText && (
          <div className="ps-announcement-card">
            <div className="ps-announcement-content">
              {event.announcementText}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantScores;
